import { useMemo, useState } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { listWaidItems } from "@/features/transcripts/api";
import { AsyncContent } from "@/features/transcripts/components/async-content";
import { useResource } from "@/features/transcripts/hooks/use-resource";
import { exportToCsv } from "@/features/transcripts/lib/export-csv";
import type { WaidItem } from "@/features/transcripts/types";
import { useTranscriptsContext } from "@/pages/client/transcripts/transcripts-layout";
import { formatCents } from "@/lib/utils";

export default function ClientIncomePage() {
  const { client, forWho, dataVersion } = useTranscriptsContext();
  const state = useResource<WaidItem[]>(
    () => listWaidItems(client.id, { forWho }),
    [client.id, forWho, dataVersion],
  );

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-foreground mb-2 text-3xl font-bold">Wage &amp; Income Summary</h1>
        <p className="text-muted-foreground max-w-2xl">
          Income items reported to the IRS for the selected tax year.
        </p>
      </div>

      <AsyncContent state={state}>
        {(items) => <IncomeTable allItems={items} clientId={client.id} />}
      </AsyncContent>
    </div>
  );
}

function IncomeTable({ allItems, clientId }: { allItems: WaidItem[]; clientId: string }) {
  const years = useMemo(
    () => Array.from(new Set(allItems.map((i) => i.taxYear))).sort((a, b) => b - a),
    [allItems],
  );
  const [yearOverride, setYearOverride] = useState<number | undefined>(undefined);
  const year = yearOverride !== undefined && years.includes(yearOverride) ? yearOverride : years[0];

  const items = useMemo(
    () => (year !== undefined ? allItems.filter((i) => i.taxYear === year) : []),
    [allItems, year],
  );
  const totalCents = items.reduce((sum, item) => sum + item.amountCents, 0);

  function handleExport() {
    exportToCsv(`${clientId}-income-${year}.csv`, items, [
      { header: "Income Type", accessor: (i) => i.incomeType },
      { header: "Payer", accessor: (i) => i.payer },
      { header: "Amount", accessor: (i) => formatCents(i.amountCents) },
    ]);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-end gap-2">
        <Select
          value={year !== undefined ? String(year) : undefined}
          onValueChange={(v) => setYearOverride(Number(v))}
        >
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => (
              <SelectItem key={y} value={String(y)}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={items.length === 0}>
          <Download /> Export CSV
        </Button>
      </div>

      <Card className="gap-0 overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead>Income Type</TableHead>
              <TableHead>Payer</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.incomeType}</TableCell>
                <TableCell className="text-muted-foreground">{item.payer}</TableCell>
                <TableCell className="text-right">{formatCents(item.amountCents)}</TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-muted-foreground text-center">
                  No income items on file for this selection.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {items.length > 0 && (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={2}>Total</TableCell>
                <TableCell className="text-right font-bold">{formatCents(totalCents)}</TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </Card>
    </div>
  );
}
